import React, { useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Message,
  Placeholder,
  SelectPicker,
  Tag,
} from 'rsuite';
import { Button as NsiButton, FilterField, FilterToolbar, Modal, useLastPresent } from '@nsi-advisory-services/components';
import PageHeader from '../components/shared/PageHeader';
import {
  useAssignOnboarding,
  useCreateEmployee,
  useCreateHiringCandidate,
  useHiringCandidates,
  useHiringStaffOptions,
  useOnboardingAssignments,
  useOnboardingCandidates,
  useRoles,
  useUpdateHiringCandidate,
} from '../hooks/useQueries';
import EmployeeForm from '../components/employees/EmployeeForm';
import { useAuth } from '../contexts/AuthContext';
import { showToast } from '../utils/toast';
import './HiringDashboardPage.css';

const STATUS_OPTIONS = [
  { label: 'Call Scheduled', value: 'Call Scheduled' },
  { label: 'Call Completed', value: 'Call Completed' },
  { label: 'On Hold', value: 'On Hold' },
  { label: 'Wait and See', value: 'Wait and See' },
  { label: 'Pass', value: 'Pass' },
  { label: 'Offer Extended', value: 'Offer Extended' },
  { label: 'Practicum Scheduled', value: 'Practicum Scheduled' },
  { label: 'Hired', value: 'Hired' },
  { label: 'Offer Declined', value: 'Offer Declined' },
  { label: 'Onboarding Started', value: 'Onboarding Started' },
  { label: 'Onboarding Completed', value: 'Onboarding Completed' },
  { label: 'Other', value: 'Other' },
];

const POTENTIAL_ROLE_OPTIONS = [
  { label: 'Consultant', value: 'Consultant' },
  { label: 'Director', value: 'Director' },
  { label: 'Tech/Tools', value: 'Tech/Tools' },
  { label: 'Other', value: 'Other' },
];

const STATUS_TAG_COLORS = {
  'Call Scheduled': 'blue',
  'Call Completed': 'cyan',
  'On Hold': 'orange',
  'Wait and See': 'violet',
  Pass: 'red',
  'Offer Extended': 'green',
  'Practicum Scheduled': 'yellow',
  Hired: 'green',
  'Offer Declined': 'red',
  Other: 'gray',
  'Onboarding Started': 'blue',
  'Onboarding Completed': 'green',
};

const Textarea = React.forwardRef(function Textarea({ value, onChange, className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      value={value ?? ''}
      onChange={(event) => onChange?.(event.target.value, event)}
      className={['rs-input', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

// A candidate is stored as one free-text name and an employee record wants two
// fields, so the first word is the given name and whatever follows is the rest
// of it. It is a starting point in a form, not a decision: the person creating
// the record sees both fields and fixes them.
const splitCandidateName = (value) => {
  const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first_name: '', last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
};

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/;

const toLocalDateValue = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const s = value.trim();
    const match = s.match(DATE_ONLY_RE);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]);
      const day = Number(match[3]);

      const localDate = new Date(year, month - 1, day);
      if (
        localDate.getFullYear() !== year
        || localDate.getMonth() !== month - 1
        || localDate.getDate() !== day
      ) {
        return null;
      }

      return localDate;
    }
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d;
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = toLocalDateValue(value);
  if (!d) return '-';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const toDateOnlyString = (value) => {
  if (!value) return null;
  const d = toLocalDateValue(value);
  if (!d) return null;
  const pad2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const toDateValue = (value) => {
  return toLocalDateValue(value);
};

const createEmptyCandidate = () => ({
  name: '',
  status: 'Call Scheduled',
  connection_to_nsi: '',
  location: '',
  current_employer: '',
  primary_competency: '',
  completed_application: false,
  potential_role: '',
  potential_start_date: null,
  follow_up_date: null,
  follow_up_staff: '',
  notes: '',
  interview_date: null,
  next_steps: '',
  application_url: '',
});

const toFormValue = (candidate) => ({
  name: String(candidate?.name || ''),
  status: String(candidate?.status || 'Call Scheduled'),
  connection_to_nsi: String(candidate?.connection_to_nsi || ''),
  location: String(candidate?.location || ''),
  current_employer: String(candidate?.current_employer || ''),
  primary_competency: String(candidate?.primary_competency || ''),
  completed_application: Boolean(candidate?.completed_application),
  potential_role: String(candidate?.potential_role || ''),
  potential_start_date: toDateValue(candidate?.potential_start_date),
  follow_up_date: toDateValue(candidate?.follow_up_date),
  follow_up_staff: String(candidate?.follow_up_staff || ''),
  notes: String(candidate?.notes || ''),
  interview_date: toDateValue(candidate?.interview_date),
  next_steps: String(candidate?.next_steps || ''),
  application_url: String(candidate?.application_url || ''),
});

const toPayload = (formValue) => ({
  name: String(formValue?.name || '').trim(),
  status: String(formValue?.status || '').trim(),
  connection_to_nsi: String(formValue?.connection_to_nsi || '').trim(),
  location: String(formValue?.location || '').trim(),
  current_employer: String(formValue?.current_employer || '').trim(),
  primary_competency: String(formValue?.primary_competency || '').trim(),
  completed_application: Boolean(formValue?.completed_application),
  potential_role: String(formValue?.potential_role || '').trim(),
  potential_start_date: toDateOnlyString(formValue?.potential_start_date),
  follow_up_date: toDateOnlyString(formValue?.follow_up_date),
  follow_up_staff: String(formValue?.follow_up_staff || '').trim(),
  notes: String(formValue?.notes || '').trim(),
  interview_date: toDateOnlyString(formValue?.interview_date),
  next_steps: String(formValue?.next_steps || '').trim(),
  application_url: String(formValue?.application_url || '').trim(),
});

function CandidateFormFields({ formValue, setFormValue, error, loading, followUpStaffOptions }) {
  const statusOptions = useMemo(() => {
    const current = String(formValue?.status || '').trim();
    if (!current) return STATUS_OPTIONS;
    if (STATUS_OPTIONS.some((item) => item.value === current)) return STATUS_OPTIONS;
    return [{ label: current, value: current }, ...STATUS_OPTIONS];
  }, [formValue?.status]);

  const potentialRoleOptions = useMemo(() => {
    const current = String(formValue?.potential_role || '').trim();
    if (!current) return POTENTIAL_ROLE_OPTIONS;
    if (POTENTIAL_ROLE_OPTIONS.some((item) => item.value === current)) return POTENTIAL_ROLE_OPTIONS;
    return [{ label: current, value: current }, ...POTENTIAL_ROLE_OPTIONS];
  }, [formValue?.potential_role]);

  const resolvedFollowUpStaffOptions = useMemo(() => {
    const current = String(formValue?.follow_up_staff || '').trim();
    const options = Array.isArray(followUpStaffOptions) ? followUpStaffOptions : [];
    if (!current) return options;
    if (options.some((item) => item.value === current)) return options;
    return [{ label: current, value: current }, ...options];
  }, [followUpStaffOptions, formValue?.follow_up_staff]);

  return (
    <div className="d-flex flex-column gap-3">
      {error ? (
        <Message type="error" showIcon>
          {String(error?.message || error)}
        </Message>
      ) : null}

      <Form fluid formValue={formValue} onChange={setFormValue}>
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-name">
              <Form.ControlLabel>Name *</Form.ControlLabel>
              <Form.Control name="name" accepter={Input} />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-status">
              <Form.ControlLabel>Status *</Form.ControlLabel>
              <Form.Control
                name="status"
                accepter={SelectPicker}
                data={statusOptions}
                cleanable={false}
                searchable={false}
                style={{ width: '100%' }}
              />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-connection-to-nsi">
              <Form.ControlLabel>Connection to NSI</Form.ControlLabel>
              <Form.Control name="connection_to_nsi" accepter={Input} />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-location">
              <Form.ControlLabel>Location</Form.ControlLabel>
              <Form.Control name="location" accepter={Input} />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-current-employer">
              <Form.ControlLabel>Current Employer</Form.ControlLabel>
              <Form.Control name="current_employer" accepter={Input} />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-primary-competency">
              <Form.ControlLabel>Primary Competency</Form.ControlLabel>
              <Form.Control name="primary_competency" accepter={Input} />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6">
            <Form.Group controlId="candidate-potential-role">
              <Form.ControlLabel>Potential Role</Form.ControlLabel>
              <Form.Control
                name="potential_role"
                accepter={SelectPicker}
                data={potentialRoleOptions}
                cleanable
                searchable={false}
                style={{ width: '100%' }}
              />
            </Form.Group>
          </div>

          <div className="col-12 col-md-6 d-flex align-items-end">
            <Checkbox
              checked={Boolean(formValue.completed_application)}
              onChange={(_value, checked) => setFormValue((prev) => ({ ...prev, completed_application: Boolean(checked) }))}
              disabled={loading}
            >
              Completed Application
            </Checkbox>
          </div>

          {formValue.completed_application ? (
            <div className="col-12">
              <Form.Group controlId="candidate-application-url">
                <Form.ControlLabel>Application URL</Form.ControlLabel>
                <Form.Control name="application_url" accepter={Input} />
              </Form.Group>
            </div>
          ) : null}

          <div className="col-12">
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <Form.Group controlId="candidate-potential-start-date">
                  <Form.ControlLabel>Potential Start Date</Form.ControlLabel>
                  <DatePicker
                    oneTap
                    isoWeek={false}
                    weekStart={0}
                    value={formValue.potential_start_date}
                    onChange={(value) => setFormValue((prev) => ({ ...prev, potential_start_date: value || null }))}
                    format="MM/dd/yyyy"
                    style={{ width: '100%' }}
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-4">
                <Form.Group controlId="candidate-follow-up-date">
                  <Form.ControlLabel>Follow Up Date</Form.ControlLabel>
                  <DatePicker
                    oneTap
                    isoWeek={false}
                    weekStart={0}
                    value={formValue.follow_up_date}
                    onChange={(value) => setFormValue((prev) => ({ ...prev, follow_up_date: value || null }))}
                    format="MM/dd/yyyy"
                    style={{ width: '100%' }}
                  />
                </Form.Group>
              </div>

              <div className="col-12 col-md-4">
                <Form.Group controlId="candidate-interview-date">
                  <Form.ControlLabel>Interview Date</Form.ControlLabel>
                  <DatePicker
                    oneTap
                    isoWeek={false}
                    weekStart={0}
                    value={formValue.interview_date}
                    onChange={(value) => setFormValue((prev) => ({ ...prev, interview_date: value || null }))}
                    format="MM/dd/yyyy"
                    style={{ width: '100%' }}
                  />
                </Form.Group>
              </div>
            </div>
          </div>

          <div className="col-12">
            <Form.Group controlId="candidate-follow-up-staff">
              <Form.ControlLabel>Follow Up Staff</Form.ControlLabel>
              <Form.Control
                name="follow_up_staff"
                accepter={SelectPicker}
                data={resolvedFollowUpStaffOptions}
                cleanable
                searchable
                style={{ width: '100%' }}
              />
            </Form.Group>
          </div>

          <div className="col-12">
            <Form.Group controlId="candidate-next-steps">
              <Form.ControlLabel>Next Steps</Form.ControlLabel>
              <Form.Control name="next_steps" accepter={Textarea} rows={3} />
            </Form.Group>
          </div>

          <div className="col-12">
            <Form.Group controlId="candidate-notes">
              <Form.ControlLabel>Notes</Form.ControlLabel>
              <Form.Control name="notes" accepter={Textarea} rows={4} />
            </Form.Group>
          </div>

        </div>
      </Form>
    </div>
  );
}

export default function HiringDashboardPage() {
  const query = useHiringCandidates({ enabled: true });
  const staffOptionsQuery = useHiringStaffOptions({ enabled: true });
  const createMutation = useCreateHiringCandidate();
  const updateMutation = useUpdateHiringCandidate();

  // Onboarding is tracked against the employee record, not the candidate, so
  // starting it asks which employee this candidate became. The employee list
  // comes from the onboarding endpoint rather than /employees: running hiring
  // does not imply permission to read the employee directory.
  const auth = useAuth();

  // Creating the employee record needs the permission that creating employees
  // needs; running hiring does not imply it. Without it the modal offers the
  // picker alone, which is what it did before.
  const canCreateEmployees = auth.hasPermission('manage_all_employees');
  const createEmployee = useCreateEmployee();
  const rolesQuery = useRoles({ enabled: canCreateEmployees });

  const onboardingCandidatesQuery = useOnboardingCandidates({ enabled: true });
  const onboardingEmployeesQuery = useOnboardingAssignments({ enabled: true });
  const assignOnboarding = useAssignOnboarding();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [createValue, setCreateValue] = useState(createEmptyCandidate());
  const [editValue, setEditValue] = useState(createEmptyCandidate());
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [onboardingTarget, setOnboardingTarget] = useState(null);
  const [onboardingEmployeeId, setOnboardingEmployeeId] = useState(null);
  const [onboardingMode, setOnboardingMode] = useState('existing');
  // The form's submit lives in the modal footer with the other buttons, so the
  // footer needs a way to run it.
  const employeeFormRef = useRef(null);
  // Keep the dialogs steady while they fade out after these are cleared.
  const onboardingShown = useLastPresent(onboardingTarget);

  const rows = useMemo(() => query.data || [], [query.data]);
  const statusFilterOptions = useMemo(() => {
    const knownStatuses = STATUS_OPTIONS.map((item) => item.value);
    const seenStatuses = new Set(
      rows
        .map((row) => String(row?.status || '').trim())
        .filter(Boolean),
    );

    const extraStatuses = [...seenStatuses]
      .filter((status) => !knownStatuses.includes(status))
      .sort((a, b) => a.localeCompare(b))
      .map((status) => ({ label: status, value: status }));

    return [
      { label: 'All Statuses', value: 'all' },
      ...STATUS_OPTIONS,
      ...extraStatuses,
    ];
  }, [rows]);
  const filteredRows = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return rows.filter((row) => {
      const statusMatches = statusFilter === 'all' || String(row?.status || '').trim() === statusFilter;
      if (!statusMatches) return false;

      if (!normalizedSearch) return true;

      const name = String(row?.name || '').toLowerCase();
      const status = String(row?.status || '').toLowerCase();
      const followUpStaff = String(row?.follow_up_staff || '').toLowerCase();
      const nextSteps = String(row?.next_steps || '').toLowerCase();
      const followUpDate = formatDate(row?.follow_up_date).toLowerCase();

      return name.includes(normalizedSearch)
        || status.includes(normalizedSearch)
        || followUpStaff.includes(normalizedSearch)
        || nextSteps.includes(normalizedSearch)
        || followUpDate.includes(normalizedSearch);
    });
  }, [rows, searchTerm, statusFilter]);
  const followUpStaffOptions = useMemo(() => {
    const rowsForOptions = Array.isArray(staffOptionsQuery.data) ? staffOptionsQuery.data : [];
    const seen = new Set();

    return rowsForOptions
      .filter((row) => row?.is_active === true)
      .map((row) => {
        const firstName = String(row?.first_name || '').trim();
        const lastName = String(row?.last_name || '').trim();
        const email = String(row?.email || '').trim();
        const baseName = `${firstName} ${lastName}`.trim() || email || 'Unknown User';
        const label = email && baseName !== email ? `${baseName} (${email})` : baseName;
        const value = label;
        if (!value || seen.has(value)) return null;
        seen.add(value);
        return { label, value };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [staffOptionsQuery.data]);

  // Progress per candidate, keyed the way the cards are rendered.
  const onboardingByCandidate = useMemo(() => {
    const map = new Map();
    (onboardingCandidatesQuery.data?.candidates || []).forEach((row) => {
      map.set(row.hiring_candidate_id, row);
    });
    return map;
  }, [onboardingCandidatesQuery.data]);

  const onboardingTotalSteps = onboardingCandidatesQuery.data?.total_steps || 0;

  const onboardingEmployeeOptions = useMemo(() => (
    (onboardingEmployeesQuery.data?.employees || []).map((row) => {
      const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.email || 'Unknown';
      return {
        value: row.employee_id,
        label: row.status ? `${name} — already onboarding` : name,
      };
    })
  ), [onboardingEmployeesQuery.data]);

  const openCreate = () => {
    setCreateValue(createEmptyCandidate());
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    setEditTarget(row);
    setEditValue(toFormValue(row));
  };

  const closeEdit = () => setEditTarget(null);

  const submitCreate = async () => {
    await createMutation.mutateAsync(toPayload(createValue));
    setCreateOpen(false);
  };

  const submitEdit = async () => {
    if (!editTarget?.id) return;
    await updateMutation.mutateAsync({ id: editTarget.id, ...toPayload(editValue) });
    closeEdit();
  };

  const openOnboarding = (row) => {
    setOnboardingTarget(row);
    setOnboardingEmployeeId(null);
    setOnboardingMode('existing');
  };

  const closeOnboarding = () => setOnboardingTarget(null);

  // Once the dialog has faded out, so its fields don't change while it is visible.
  const resetOnboarding = () => {
    setOnboardingEmployeeId(null);
    setOnboardingMode('existing');
  };

  /*
    What the hiring board knows about the candidate, in the shape the employee
    form takes. Everything here is editable in the form — this only saves the
    person creating the record from typing what the board already holds.
  */
  const onboardingInitialEmployee = useMemo(() => {
    if (!onboardingShown) return null;

    return {
      ...splitCandidateName(onboardingShown.name),
      job_title: onboardingShown.potential_role || '',
      // A Date built in local time. The form passes what it is given through
      // `new Date(...)`, and a bare 'YYYY-MM-DD' would be read as UTC midnight
      // and show the day before west of Greenwich.
      hire_date: toLocalDateValue(onboardingShown.potential_start_date),
    };
  }, [onboardingShown]);

  /*
    Create the employee record and start onboarding against it, in that order.

    If the second call fails the record still exists and is a real employee —
    nothing to undo — and the board's own button will pick it up through the
    existing-record path next time.
  */
  const submitOnboardingWithNewEmployee = async (payload) => {
    if (!onboardingTarget?.id) return;

    try {
      const employee = await createEmployee.mutateAsync(payload);
      await assignOnboarding.mutateAsync({
        employeeId: employee.id,
        hiringCandidateId: onboardingTarget.id,
      });
      showToast('success', `Employee record created and onboarding started for ${onboardingTarget.name || 'this candidate'}.`);
      closeOnboarding();
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to create the employee record'));
    }
  };

  const submitOnboarding = async () => {
    if (!onboardingTarget?.id || !onboardingEmployeeId) return;
    try {
      await assignOnboarding.mutateAsync({
        employeeId: onboardingEmployeeId,
        hiringCandidateId: onboardingTarget.id,
      });
      showToast('success', `Onboarding started for ${onboardingTarget.name || 'this candidate'}.`);
      closeOnboarding();
    } catch (err) {
      showToast('error', String(err?.message || err || 'Unable to start onboarding'));
    }
  };

  if (query.isLoading) {
    return <Placeholder.Paragraph rows={7} active />;
  }

  return (
    <div className="bdc-page">
      <PageHeader
        crumbs={[
          { label: 'Home', to: '/' },
          { label: 'Admin', to: '/admin' },
          { label: 'Hiring' },
        ]}
        icon="bi-person-workspace"
        title="Hiring Dashboard"
        actions={(
          <>
            <button type="button" className="bdc-btn bdc-btn--primary" onClick={openCreate}>
              <i className="bi bi-plus-lg" aria-hidden="true" />
              New Candidate
            </button>
          </>
        )}
      />

      <FilterToolbar>
        <FilterField label="Search" basis={320} max={400}>
          <input
            type="search"
            className="form-control"
            placeholder="Search name, staff, status, next steps"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search candidates"
          />
        </FilterField>

        <FilterField label="Status" basis={280} max={340}>
          <SelectPicker
            data={statusFilterOptions}
            value={statusFilter}
            onChange={(value) => setStatusFilter(String(value || 'all'))}
            cleanable
            searchable
            block
          />
        </FilterField>
      </FilterToolbar>

      {query.error ? (
        <Message type="error" showIcon>
          {String(query.error?.message || query.error)}
        </Message>
      ) : null}

      {staffOptionsQuery.error ? (
        <Message type="warning" showIcon>
          {String(staffOptionsQuery.error?.message || staffOptionsQuery.error || 'Unable to load staff options for follow up assignments.')}
        </Message>
      ) : null}

      {filteredRows.length === 0 ? (
        <Message type="info" showIcon>
          {statusFilter !== 'all'
            ? `No candidates found for status "${statusFilter}"${searchTerm.trim() ? ` matching "${searchTerm.trim()}"` : ''}.`
            : (searchTerm.trim() ? `No candidates match "${searchTerm.trim()}".` : 'No candidates found yet.')}
        </Message>
      ) : (
        <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
          {filteredRows.map((row) => {
            const status = String(row?.status || '').trim() || 'Other';
            const statusColor = STATUS_TAG_COLORS[status] || 'gray';
            const onboarding = onboardingByCandidate.get(row.id) || null;

            return (
              <div className="col" key={row.id}>
                <div className="card h-100 hiring-card">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div className="fw-semibold" style={{ lineHeight: 1.2 }}>
                          {row.name || 'Unnamed Candidate'}
                        </div>
                      </div>
                      <Tag color={statusColor}>{status}</Tag>
                    </div>

                    <div className="hiring-card-meta-grid mt-3">
                      <div>
                        <div className="hiring-card-meta-label">Follow Up Date</div>
                        <div className="hiring-card-meta-value">{formatDate(row.follow_up_date)}</div>
                      </div>
                      <div>
                        <div className="hiring-card-meta-label">Follow Up Staff</div>
                        <div className="hiring-card-meta-value">{row.follow_up_staff || '-'}</div>
                      </div>
                      <div>
                        <div className="hiring-card-meta-label">Next Steps</div>
                        <div className="hiring-card-meta-value">{row.next_steps || '-'}</div>
                      </div>
                      {onboarding ? (
                        <div>
                          <div className="hiring-card-meta-label">Onboarding</div>
                          <div className="hiring-card-meta-value">
                            {onboarding.employee_name}
                            {' · '}
                            <span className="bdc-num">
                              {onboarding.completed_count} / {onboardingTotalSteps}
                            </span>
                            {' steps'}
                          </div>
                        </div>
                      ) : null}
                    </div>

                    <div className="hiring-card-actions mt-auto pt-3 d-flex flex-wrap gap-1">
                      <Button size="xs" appearance="ghost" onClick={() => openEdit(row)}>Edit</Button>
                      {onboarding ? (
                        <Button
                          size="xs"
                          appearance="ghost"
                          disabled
                          title={`Onboarding ${onboarding.status === 'completed' ? 'complete' : 'in progress'} as ${onboarding.employee_name}`}
                        >
                          {onboarding.status === 'completed' ? 'Onboarding Complete' : 'Onboarding In Progress'}
                        </Button>
                      ) : (
                        <Button
                          size="xs"
                          appearance="ghost"
                          color="orange"
                          onClick={() => openOnboarding(row)}
                        >
                          Start Onboarding
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onExited={() => setCreateValue(createEmptyCandidate())}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>New Candidate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CandidateFormFields
            formValue={createValue}
            setFormValue={setCreateValue}
            loading={createMutation.isPending}
            error={createMutation.error}
            followUpStaffOptions={followUpStaffOptions}
          />
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setCreateOpen(false)}>Cancel</NsiButton>
          <NsiButton variant="primary" onClick={submitCreate} loading={createMutation.isPending}>Save</NsiButton>
        </Modal.Footer>
      </Modal>

      <Modal
        open={!!onboardingTarget}
        onClose={closeOnboarding}
        onExited={resetOnboarding}
        size={onboardingMode === 'create' ? 'md' : 'xs'}
      >
        <Modal.Header>
          <Modal.Title>Start Onboarding</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="bdc-modal__fields">
            <div className="hiring-card-meta-value">
              Onboarding is tracked against an employee record, so
              {' '}{onboardingShown?.name || 'this candidate'} needs one before it can start.
            </div>

            {canCreateEmployees ? (
              <div className="bdc-segment bdc-segment--block">
                <button
                  type="button"
                  className={`bdc-btn bdc-btn--sm${onboardingMode === 'existing' ? ' bdc-btn--primary' : ''}`}
                  onClick={() => setOnboardingMode('existing')}
                >
                  Use an existing record
                </button>
                <button
                  type="button"
                  className={`bdc-btn bdc-btn--sm${onboardingMode === 'create' ? ' bdc-btn--primary' : ''}`}
                  onClick={() => setOnboardingMode('create')}
                >
                  Create the record now
                </button>
              </div>
            ) : null}

            {assignOnboarding.error ? (
              <Message type="error" showIcon>
                {String(assignOnboarding.error?.message || assignOnboarding.error)}
              </Message>
            ) : null}

            {onboardingMode === 'create' ? (
              <EmployeeForm
                ref={employeeFormRef}
                initialValue={onboardingInitialEmployee}
                roles={rolesQuery.data || []}
                onSubmit={submitOnboardingWithNewEmployee}
                loading={createEmployee.isPending || assignOnboarding.isPending}
                error={createEmployee.error}
                hideSubmit
              />
            ) : (
              <div>
                <div className="bdc-modal__label">Employee</div>
                <SelectPicker
                  data={onboardingEmployeeOptions}
                  value={onboardingEmployeeId}
                  onChange={setOnboardingEmployeeId}
                  searchable
                  block
                  placeholder="Select an employee"
                />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeOnboarding}>Cancel</NsiButton>
          {onboardingMode === 'create' ? (
            <NsiButton
              variant="primary"
              onClick={() => employeeFormRef.current?.submit()}
              loading={createEmployee.isPending || assignOnboarding.isPending}
            >
              Create and start onboarding
            </NsiButton>
          ) : (
            <NsiButton
              variant="primary"
              onClick={submitOnboarding}
              loading={assignOnboarding.isPending}
              disabled={!onboardingEmployeeId}
            >
              Start Onboarding
            </NsiButton>
          )}
        </Modal.Footer>
      </Modal>

      <Modal
        open={!!editTarget}
        onClose={closeEdit}
        onExited={() => setEditValue(createEmptyCandidate())}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>Edit Candidate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CandidateFormFields
            formValue={editValue}
            setFormValue={setEditValue}
            loading={updateMutation.isPending}
            error={updateMutation.error}
            followUpStaffOptions={followUpStaffOptions}
          />
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={closeEdit}>Cancel</NsiButton>
          <NsiButton variant="primary" onClick={submitEdit} loading={updateMutation.isPending}>Save</NsiButton>
        </Modal.Footer>
      </Modal>

    </div>
  );
}
